import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiErrorDetail } from "../lib/api";

/** Handles the return leg of Emergent-managed Google Auth: exchanges the one-time
 * session_id from the URL hash for our standard JWT session, then goes to the dashboard. */
export const GoogleCallback = () => {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false); // ref (not state): survives StrictMode double-invoke

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    // The auth provider redirected back to the page the user originally wanted — return there after the exchange.
    const path = window.location.pathname + window.location.search;
    const dest = path !== "/" && path !== "/login" ? path : "/dashboard";
    // Clean the fragment so the one-time session_id never lingers in history
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    googleSession(sessionId)
      .then(() => navigate(dest, { replace: true }))
      .catch((e) => {
        toast.error(apiErrorDetail(e));
        navigate("/login", { replace: true });
      });
  }, [googleSession, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground"
      data-testid="google-callback-loading">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Completing Google sign-in…
    </div>
  );
};
